import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getMyPermissions } from '../api/benutzerApi';

const PermissionContext = createContext(null);

// Alle verfügbaren Module
export const MODULES = {
  DASHBOARD: 'DASHBOARD',
  KUNDEN: 'KUNDEN',
  ANFRAGEN: 'ANFRAGEN',
  TERMINE: 'TERMINE',
  PROJEKTE: 'PROJEKTE',
  RECHNUNGEN: 'RECHNUNGEN',
  KOSTENVORANSCHLAEGE: 'KOSTENVORANSCHLAEGE',
  ZEITERFASSUNG: 'ZEITERFASSUNG',
  KATALOG: 'KATALOG',
  EINSTELLUNGEN: 'EINSTELLUNGEN',
  KI_ASSISTENT: 'KI_ASSISTENT',
  KI_BENACHRICHTIGUNGEN: 'KI_BENACHRICHTIGUNGEN',
  KI_VORSCHLAEGE: 'KI_VORSCHLAEGE',
  ABWESENHEITEN: 'ABWESENHEITEN',
  BESTELLUNGEN: 'BESTELLUNGEN',
  PLANTAFEL: 'PLANTAFEL'
};

// Module Labels für UI
export const MODULE_LABELS = {
  DASHBOARD: 'Dashboard',
  KUNDEN: 'Kunden',
  ANFRAGEN: 'Anfragen',
  TERMINE: 'Kalender',
  PROJEKTE: 'Projekte',
  RECHNUNGEN: 'Rechnungen',
  KOSTENVORANSCHLAEGE: 'Kostenvoranschläge',
  ZEITERFASSUNG: 'Zeiterfassung',
  KATALOG: 'Katalog',
  EINSTELLUNGEN: 'Einstellungen',
  KI_ASSISTENT: 'KI-Assistent',
  KI_BENACHRICHTIGUNGEN: 'KI-Benachrichtigungen',
  KI_VORSCHLAEGE: 'KI-Vorschläge',
  ABWESENHEITEN: 'Abwesenheiten',
  BESTELLUNGEN: 'Bestellungen',
  PLANTAFEL: 'Plantafel'
};

export function PermissionProvider({ children }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Berechtigungen laden
  const loadPermissions = useCallback(async () => {
    if (!user) {
      setPermissions({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getMyPermissions();
      setPermissions(data.permissions || {});
      setError(null);
    } catch (err) {
      console.error('Fehler beim Laden der Berechtigungen:', err);
      setError('Berechtigungen konnten nicht geladen werden');
      // Bei Admin-Rolle: Vollzugriff als Fallback
      if (user?.rolle === 'admin') {
        const fullAccess = {};
        Object.keys(MODULES).forEach(modul => {
          fullAccess[modul] = {
            canView: true,
            canCreate: true,
            canEdit: true,
            canDelete: true
          };
        });
        setPermissions(fullAccess);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  /**
   * Prüft ob der Benutzer eine bestimmte Berechtigung hat
   * @param {string} modul - Das Modul (z.B. 'KUNDEN')
   * @param {string} action - Die Aktion ('canView', 'canCreate', 'canEdit', 'canDelete')
   * @returns {boolean}
   */
  const hasPermission = useCallback((modul, action = 'canView') => {
    // Admins haben immer Vollzugriff
    if (user?.rolle === 'admin') {
      return true;
    }

    const modulPerms = permissions[modul];
    if (!modulPerms) return false;

    return modulPerms[action] === true;
  }, [user, permissions]);

  /**
   * Prüft ob der Benutzer ein Modul ansehen kann
   */
  const canView = useCallback((modul) => hasPermission(modul, 'canView'), [hasPermission]);

  /**
   * Prüft ob der Benutzer in einem Modul erstellen kann
   */
  const canCreate = useCallback((modul) => hasPermission(modul, 'canCreate'), [hasPermission]);

  /**
   * Prüft ob der Benutzer in einem Modul bearbeiten kann
   */
  const canEdit = useCallback((modul) => hasPermission(modul, 'canEdit'), [hasPermission]);

  /**
   * Prüft ob der Benutzer in einem Modul löschen kann
   */
  const canDelete = useCallback((modul) => hasPermission(modul, 'canDelete'), [hasPermission]);

  /**
   * Gibt alle Berechtigungen für ein Modul zurück
   */
  const getModulePermissions = useCallback((modul) => {
    if (user?.rolle === 'admin') {
      return {
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: true
      };
    }
    return permissions[modul] || {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false
    };
  }, [user, permissions]);

  /**
   * Prüft ob der Benutzer Admin ist
   */
  const isAdmin = user?.rolle === 'admin';

  const value = {
    permissions,
    loading,
    error,
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    getModulePermissions,
    isAdmin,
    refreshPermissions: loadPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions muss innerhalb eines PermissionProvider verwendet werden');
  }
  return context;
}

export default PermissionContext;
